import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Subject, takeUntil } from 'rxjs';

import { Button } from '@sololearnorg/sol';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Container, track } from '../../../../../../symphony';

import { SelectInput, TextInput } from '../Inputs/Inputs';
import { MailingContext } from '../../../../../private/contexts';
import { MailingDataService } from '../../../../../private/services/mailing/mailing-data.service';
import {
	MailingActionTypes,
	MailingActionsService,
} from '../../../../../private/services/mailing/mailing-actions.service';

import s from './ContactForm.scss';
import { ITeamsRequestData } from '../../../../../../api/public/mailing-api';
import { useContainerData } from '../../../../../../shared/public/SlHooks/useContainerData';
import { useIsVisibleHook } from '../../../../../../shared/public/SlHooks/useIsVisibleHook';
import { validateName, validateEmail, validatePhoneNumber } from '../../../../../../shared/public/utilsPublic/utilsPublic';

const schema = yup.object().shape({
	firstName: validateName(),
	lastName: validateName(),
	companyName: yup.string().required('Please enter company name'),
	jobTitle: yup.string(),
	workEmail: validateEmail(),
	phoneNumber: validatePhoneNumber(),
	learnersRange: yup.string().required('Please enter something'),
});

export function ContactForm() {
	useStyles(s);
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ITeamsRequestData>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(schema),
		defaultValues: {
			firstName: '',
			lastName: '',
			companyName: '',
			jobTitle: '',
			workEmail: '',
			phoneNumber: '',
			learnersRange: '1-100',
		},
	});

	const { teamsLoading$: loading, teamsModal$: modal } = useContainerData(
		MailingContext,
		MailingDataService,
		['teamsLoading$', 'teamsModal$'],
	);

	const ref = useRef(null);

	const isVisible = useIsVisibleHook(ref, 0, false, '80% 0px 0px');

	const onSubmit = (payload: ITeamsRequestData) => {
		const { actions$ } = Container.take(
			MailingContext,
			MailingActionsService,
		);

		actions$.next({
			type: MailingActionTypes.sendTeamsRequest,
			payload,
		});

		track({
			event_name: 'demo_request_click',
			cta_type: 'REQUEST A DEMO',
			name: `${payload.firstName} ${payload.lastName}`,
			company_email: payload.workEmail,
			phone_number: payload.phoneNumber,
			company_name: payload.companyName,
			size: payload.learnersRange,
		});
	};

	useEffect(() => {
		const unsubscriber$ = new Subject<void>();

		const { actionsStream } = Container.take(
			MailingContext,
			MailingActionsService,
		);

		actionsStream.pipe(takeUntil(unsubscriber$)).subscribe();

		return () => {
			unsubscriber$.next();
			unsubscriber$.complete();
			Container.reset(MailingContext);
		};
	}, []);

	useEffect(() => {
		if (modal === 'success') {
			reset();
		}
	}, [modal]);

	useEffect(() => {
		if (isVisible) {
			track({
				event_name: 'landing_page_impression',
				version: '2-0-0',
				experience_type_id: '5',
				experience_alias: 'SololearnTeams',
				section_type: 'CONTACT FORM',
				section_title: 'sololearn_teams_contact_form_v1',
				url_text: location.pathname,
			});
		}
	}, [isVisible]);

	return (
		<form
			className="sl-teams-contact-form"
			onSubmit={handleSubmit(onSubmit)}
			ref={ref}
		>
			<div className="sl-teams-contact-form__name">
				<TextInput
					id="firstName"
					label="First name *"
					register={register}
					error={errors.firstName?.message}
				/>
				<TextInput
					id="lastName"
					label="Last name *"
					register={register}
					error={errors.lastName?.message}
				/>
			</div>
			<TextInput
				id="companyName"
				label="Company name *"
				register={register}
				error={errors.companyName?.message}
			/>
			<TextInput
				id="jobTitle"
				label="Your job title"
				register={register}
			/>
			<TextInput
				id="workEmail"
				label="Your work email *"
				register={register}
				error={errors.workEmail?.message}
			/>
			<TextInput
				id="phoneNumber"
				label="Phone number"
				register={register}
				error={errors.phoneNumber?.message}
			/>
			<SelectInput
				id="learnersRange"
				label="Approx. number of learners *"
				register={register}
				error={errors.learnersRange?.message}
				options={[
					{
						value: '1-100',
						label: '1-100',
					},
					{
						value: '101-1000',
						label: '101-1000',
					},
					{
						value: '1001-10000',
						label: '1001-10000',
					},
					{
						value: '10001+',
						label: '10001+',
					},
				]}
			/>
			<Button
				type="submit"
				className="sl-teams-contact-form__submit"
				loading={loading}
			>
				Request your demo
			</Button>
		</form>
	);
}
